import type { PropertyResponse } from '~/services/AccommodationService';

// hasBundle/hasCampaign are indicativePropertyState-backed query filters like the rest here, but
// aren't surfaced in the indicativePropertyState response payload itself (the response instead
// exposes hasAttachedBundles/hasAttachedCampaigns directly on the property), so they can't be
// derived from PropertyResponse['indicativePropertyState'] the way the others are.
export type IndicativePropertyStateFlag =
  | keyof PropertyResponse['indicativePropertyState']
  | 'hasBundle'
  | 'hasCampaign';

export type IndicativePropertyStateFilterOption = {
  flag: IndicativePropertyStateFlag;
  value: boolean;
  label: string;
};

export const RATES_ENABLED_FILTER_OPTIONS: Array<IndicativePropertyStateFilterOption> = [
  { flag: 'hasBedbankRatesEnabled', value: true, label: 'Bedbank rates enabled' },
  { flag: 'hasBedbankRatesEnabled', value: false, label: 'Bedbank rates disabled' },
  { flag: 'hasLpcRatesEnabled', value: true, label: 'LPC rates enabled' },
  { flag: 'hasLpcRatesEnabled', value: false, label: 'LPC rates disabled' },
];

export const SUPPLIER_STATE_FILTER_OPTIONS: Array<IndicativePropertyStateFilterOption> = [
  { flag: 'hasBedbankRoomsMapped', value: true, label: 'Bedbank rooms mapped' },
  { flag: 'hasBedbankRoomsMapped', value: false, label: 'Bedbank rooms unmapped' },
  { flag: 'hasLpcOffer', value: true, label: 'LPC offer linked' },
  { flag: 'hasLpcOffer', value: false, label: 'LPC offer missing' },
  { flag: 'hasApprovedLpcOffer', value: true, label: 'LPC offer approved' },
  { flag: 'hasApprovedLpcOffer', value: false, label: 'LPC offer not approved' },
  { flag: 'hasScheduledLpcOffer', value: true, label: 'LPC offer scheduled' },
  { flag: 'hasScheduledLpcOffer', value: false, label: 'LPC offer unscheduled' },
  { flag: 'hasLpcRoomsMapped', value: true, label: 'LPC rooms mapped' },
  { flag: 'hasLpcRoomsMapped', value: false, label: 'LPC rooms unmapped' },
  { flag: 'hasFlashOffer', value: true, label: 'Flash offer linked' },
  { flag: 'hasFlashOffer', value: false, label: 'No flash offer' },
  { flag: 'hasApprovedFlashOffer', value: true, label: 'Flash offer approved' },
  { flag: 'hasApprovedFlashOffer', value: false, label: 'Flash offer not approved' },
  { flag: 'hasScheduledFlashOffer', value: true, label: 'Flash offer scheduled' },
  { flag: 'hasScheduledFlashOffer', value: false, label: 'Flash offer unscheduled' },
  { flag: 'hasUltraLuxSupplier', value: true, label: 'UltraLux supplier' },
  { flag: 'hasUltraLuxSupplier', value: false, label: 'No UltraLux supplier' },
];

export const PROMOTIONS_FILTER_OPTIONS: Array<IndicativePropertyStateFilterOption> = [
  { flag: 'hasBundle', value: true, label: 'Bundle attached' },
  { flag: 'hasBundle', value: false, label: 'No bundle attached' },
  { flag: 'hasCampaign', value: true, label: 'Campaign attached' },
  { flag: 'hasCampaign', value: false, label: 'No campaign attached' },
];

export const INDICATIVE_PROPERTY_STATE_FILTER_OPTIONS = [
  ...RATES_ENABLED_FILTER_OPTIONS,
  ...SUPPLIER_STATE_FILTER_OPTIONS,
  ...PROMOTIONS_FILTER_OPTIONS,
];

export const getIndicativePropertyStateFilterToken = ({
  flag,
  value,
}: Pick<IndicativePropertyStateFilterOption, 'flag' | 'value'>) => `${flag}:${value}`;

const getFilterOptionsByToken = (options: Array<IndicativePropertyStateFilterOption>) =>
  new Map(options.map((option) => [getIndicativePropertyStateFilterToken(option), option]));

const FILTER_OPTIONS_BY_TOKEN = getFilterOptionsByToken(INDICATIVE_PROPERTY_STATE_FILTER_OPTIONS);

export const normalizeIndicativePropertyStateFilters = (
  value?: string,
  options: Array<IndicativePropertyStateFilterOption> = INDICATIVE_PROPERTY_STATE_FILTER_OPTIONS,
) => {
  const optionsByToken = getFilterOptionsByToken(options);

  return (value ?? '')
    .split(',')
    .map((token) => token.trim())
    .filter((token) => optionsByToken.has(token))
    .join(',');
};

export const getSelectedIndicativePropertyStateFilterOptions = (value?: string) =>
  normalizeIndicativePropertyStateFilters(value)
    .split(',')
    .filter(Boolean)
    .map((token) => FILTER_OPTIONS_BY_TOKEN.get(token))
    .filter((option): option is IndicativePropertyStateFilterOption => Boolean(option));

export const getIndicativePropertyStateQueryFilters = (value?: string) => {
  const selectedOptions = getSelectedIndicativePropertyStateFilterOptions(value);

  const filtersByFlag = new Map<IndicativePropertyStateFlag, Set<boolean>>();
  for (const option of selectedOptions) {
    const values = filtersByFlag.get(option.flag) ?? new Set<boolean>();
    values.add(option.value);
    filtersByFlag.set(option.flag, values);
  }

  const queryFilters: Partial<Record<IndicativePropertyStateFlag, boolean>> = {};
  for (const [flag, values] of filtersByFlag) {
    if (values.size === 1) {
      queryFilters[flag] = values.has(true);
    }
  }

  return queryFilters;
};
